import { useState } from 'react';
import { FaPlus } from 'react-icons/fa';
import { MdDelete } from 'react-icons/md';
import Input from 'components/Common/Input';
import CopyIcon from 'assets/icons/CopyIcon';
import Button from 'components/Common/Button';
import TelInput from 'components/Common/TelInput';
import { useFieldArray, useForm } from 'react-hook-form';
import {
  useCreateInboundSipTrunk,
  useUpdateInboundSipTrunk
} from 'apis/business/agent/deployPhone/inboundSipTrunk/hook';
import { InboundSipTrunkResponse } from 'apis/business/agent/deployPhone/inboundSipTrunk/types';
import { ClickAwayListener, IconButton, InputAdornment, Tooltip, Typography, Box } from '@mui/material';
import Radio from 'components/Common/Radio';
import Grid from '@mui/material/Grid2';

type ByocInboundSipTrunkFormProps = {
  selectedProvider: string;
  inboundFormData?: InboundSipTrunkResponse;
  telephonyProviderId?: number;
  /** When set, skips agent SIP APIs and calls this instead (e.g. business Telephony drawer). */
  persist?: (payload: InboundSipTrunkResponse) => Promise<void>;
};

/** Accept string[] or `{ value }[]` so TelInput always gets a string. */
function toTelFieldValues(items?: unknown): { value: string }[] {
  if (!Array.isArray(items) || items.length === 0) return [{ value: '' }];
  return items.map((item) => {
    if (typeof item === 'string' || typeof item === 'number') return { value: String(item) };
    if (item && typeof item === 'object' && 'value' in item) {
      const raw = (item as { value?: unknown }).value;
      return { value: raw == null ? '' : String(raw) };
    }
    return { value: '' };
  });
}

export default function ByocInboundSipTrunkForm({
  selectedProvider,
  inboundFormData,
  telephonyProviderId,
  persist
}: ByocInboundSipTrunkFormProps) {
  const [open, setOpen] = useState(false);
  const isSipConnectivity = selectedProvider === 'sip_connectivity';
  const originationUri = import.meta.env.VITE_APP_ORIGINATION_URI;

  const { mutateAsync: createInboundFormData, isPending: creatingInboundSip } = useCreateInboundSipTrunk();
  const { mutateAsync: updateInboundFormData, isPending: updatingInboundSip } = useUpdateInboundSipTrunk();

  const { control, handleSubmit, watch } = useForm<InboundSipTrunkResponse>({
    defaultValues: {
      additional_config: {
        number_type: 'phone',
        sip_trunk_id: '',
        numbers: [{ value: '' }],
        auth_password: '',
        auth_username: '',
        allowed_numbers: [''],
        ringing_timeout: '',
        media_encryption: '',
        allowed_addresses: [{ value: '' }],
        max_call_duration: ''
      }
    },
    values: {
      ...inboundFormData,
      additional_config: {
        ...inboundFormData?.additional_config,
        number_type: inboundFormData?.additional_config?.number_type ?? 'phone',
        numbers: toTelFieldValues(inboundFormData?.additional_config?.numbers),
        allowed_addresses: toTelFieldValues(inboundFormData?.additional_config?.allowed_addresses)
      }
    }
  });

  const numberType = watch('additional_config.number_type');

  const {
    fields: customPhoneNumbers,
    append: phoneNumberFieldAppend,
    remove: phoneNumberFieldRemove
  } = useFieldArray({
    control,
    name: 'additional_config.numbers'
  });

  const {
    fields: customAddresses,
    append: addressFieldAppend,
    remove: addressFieldRemove
  } = useFieldArray({
    control,
    name: 'additional_config.allowed_addresses'
  });

  const handleTooltipClose = () => setOpen(false);
  const handleTooltipOpen = () => {
    setOpen(true);
    navigator?.clipboard?.writeText(String(originationUri));
  };

  const onSubmit = async (data: InboundSipTrunkResponse) => {
    const payload: InboundSipTrunkResponse = {
      ...data,
      agent_telephony: 'byoc',
      additional_config: {
        ...data.additional_config,
        type: 'sip_trunking',
        numbers: data?.additional_config?.numbers
          ?.map((n: { value: string }) => n.value)
          .filter((val: string) => val.trim() !== ''),
        allowed_addresses: data?.additional_config?.allowed_addresses
          ?.map((a: { value: string }) => a.value)
          .filter((val: string) => val.trim() !== '')
      },
      ...(telephonyProviderId && { telephony_provider_id: telephonyProviderId })
    };

    !isSipConnectivity && delete payload?.additional_config?.allowed_addresses;

    if (persist) {
      await persist(payload);
      return;
    }

    if (inboundFormData?.id) {
      await updateInboundFormData({ trunk: { id: data.id, provider: selectedProvider, ...payload } });
    } else {
      await createInboundFormData({ trunk: { provider: selectedProvider, ...payload } });
    }
  };

  return (
    <>
      <Grid container spacing={1} borderRadius={2}>
        {selectedProvider === 'jambonz' ? (
          <Box width='100%'>
            <Box>
              <Radio
                name='additional_config.number_type'
                control={control}
                label=' '
                options={[
                  { label: 'Phone', value: 'phone' },
                  { label: 'Extension', value: 'extension' }
                ]}
              />
            </Box>
            {numberType === 'extension' ? (
              <Input
                name='additional_config.numbers[0].value'
                key={numberType}
                sx={{ backgroundColor: 'white' }}
                control={control}
                placeholder='e.g. 401'
                label='Phone Number'
                fullWidth
                required
              />
            ) : (
              <TelInput
                name='additional_config.numbers[0].value'
                key={numberType}
                sx={{ backgroundColor: 'white' }}
                control={control}
                placeholder='e.g. +1234567890'
                defaultCountry='US'
                label='Phone Number'
                fullWidth
                required
              />
            )}
            <Box mt={2}>
              <Input
                control={control}
                fullWidth
                label='Friendly Name'
                name='name'
                sx={{ backgroundColor: 'white' }}
                placeholder='e.g. Linha Commercial'
              />
              <Typography fontSize='12px' color='text.secondary' mt={0.5}>
                Shown instead of the number in your lists. You can change it anytime.
              </Typography>
            </Box>
          </Box>
        ) : !isSipConnectivity ? (
          <>
            <Grid size={12}>
              <TelInput
                name='additional_config.numbers[0].value'
                control={control}
                placeholder='+1416676230'
                defaultCountry='US'
                sx={{ backgroundColor: 'white' }}
                label='Phone Number'
                fullWidth
                required
              />
            </Grid>
            <Grid size={12}>
              <Input
                control={control}
                fullWidth
                label='Friendly Name'
                name='name'
                sx={{ backgroundColor: 'white' }}
                placeholder='e.g. Linha Commercial'
              />
              <Typography fontSize='12px' color='text.secondary' mt={0.5}>
                Shown instead of the number in your lists. You can change it anytime.
              </Typography>
            </Grid>
          </>
        ) : (
          <>
            <Grid size={12}>
              {customPhoneNumbers.map((field, index) => (
                <Box key={field.id} display='flex' alignItems='flex-start' mb={2}>
                  <Box flex={1}>
                    <TelInput
                      name={`additional_config.numbers[${index}].value`}
                      control={control}
                      placeholder='+1'
                      defaultCountry='US'
                      label='Phone Number'
                      sx={{ backgroundColor: 'white' }}
                      fullWidth
                      required
                    />
                    {index === customPhoneNumbers.length - 1 && (
                      <Typography fontSize='12px' color='text.secondary' mt={0.5}>
                        List of provider phone numbers associated with the trunk that can be used as a caller ID. Can be
                        multiple.
                      </Typography>
                    )}
                  </Box>
                  <IconButton
                    size='large'
                    sx={{
                      ml: 2,
                      mt: 0.5,
                      border: '1px solid',
                      borderColor: index === customPhoneNumbers.length - 1 ? 'primary.main' : 'error.main',
                      color: index === customPhoneNumbers.length - 1 ? 'primary.main' : 'error.main',
                      backgroundColor: 'white',
                      width: 48,
                      height: 48
                    }}
                    onClick={() => {
                      if (index === customPhoneNumbers.length - 1) {
                        phoneNumberFieldAppend({ value: '' });
                      } else {
                        phoneNumberFieldRemove(index);
                      }
                    }}
                  >
                    {index === customPhoneNumbers.length - 1 ? <FaPlus size={18} /> : <MdDelete size={18} />}
                  </IconButton>
                </Box>
              ))}
            </Grid>

            <Grid size={12}>
              <Input
                control={control}
                fullWidth
                label='Friendly Name'
                name='name'
                sx={{ backgroundColor: 'white' }}
                placeholder='e.g. Linha Commercial'
              />
              <Typography fontSize='12px' color='text.secondary' mt={0.5} mb={2}>
                Shown instead of the number in your lists. You can change it anytime.
              </Typography>
            </Grid>

            <Grid size={12}>
              {customAddresses.map((field, index) => (
                <Box key={field.id} display='flex' alignItems='flex-start' mb={2}>
                  <Box flex={1}>
                    <Input
                      control={control}
                      fullWidth
                      sx={{ backgroundColor: 'white' }}
                      name={`additional_config.allowed_addresses[${index}].value`}
                      placeholder='192.223.235.2.7/24 or 192.168.1.1'
                      label='Allowed Addresses (Optional)'
                    />
                    {index === customAddresses.length - 1 && (
                      <Typography fontSize='12px' color='text.secondary' mt={0.5}>
                        List of IP addresses that are allowed to use the trunk. Each item can be an individual IP
                        address or a CIDR block.
                      </Typography>
                    )}
                  </Box>
                  <IconButton
                    size='large'
                    sx={{
                      ml: 2,
                      mt: 0.5,
                      border: '1px solid',
                      borderColor: index === customAddresses.length - 1 ? 'primary.main' : 'error.main',
                      color: index === customAddresses.length - 1 ? 'primary.main' : 'error.main',
                      backgroundColor: 'white',
                      width: 48,
                      height: 48
                    }}
                    onClick={() => {
                      if (index === customAddresses.length - 1) {
                        addressFieldAppend({ value: '' });
                      } else {
                        addressFieldRemove(index);
                      }
                    }}
                  >
                    {index === customAddresses.length - 1 ? <FaPlus size={18} /> : <MdDelete size={18} />}
                  </IconButton>
                </Box>
              ))}
            </Grid>

            <Grid size={12} mb={2}>
              <Input
                control={control}
                fullWidth
                sx={{ backgroundColor: 'white' }}
                label='Auth Username (Optional)'
                name='additional_config.auth_username'
                placeholder='Username for authorized use of the SIP trunk'
              />
              <Typography fontSize='12px' color='text.secondary' mt={0.5}>
                If configured, the username for authorized use of the provider&apos;s SIP trunk.
              </Typography>
            </Grid>

            <Grid size={12} mb={2}>
              <Input
                control={control}
                fullWidth
                type='password'
                sx={{ backgroundColor: 'white' }}
                label='Auth Password (Optional)'
                name='additional_config.auth_password'
                placeholder='Username for authorized use of the SIP trunk'
              />
              <Typography fontSize='12px' color='text.secondary' mt={0.5}>
                If configured, the username for authorized use of the provider&apos;s SIP trunk.
              </Typography>
            </Grid>
          </>
        )}

        {selectedProvider === 'jambonz' && numberType === 'extension' ? (
          <Grid size={12}>
            <Input
              control={control}
              key={selectedProvider}
              fullWidth
              placeholder='The name of the Carrier you configured in jambonz'
              name='additional_config.carrier_name'
              sx={{ backgroundColor: 'white' }}
              label='Carrier Name'
              required
            />
            <Typography fontSize='12px' color='text.secondary' lineHeight={1.2} marginTop={0.8}>
              The name of the Carrier you configured in jambonz towards your SIP service provider
            </Typography>
          </Grid>
        ) : (
          <Grid size={12} mb={2}>
            <Input
              value={originationUri}
              control={control}
              fullWidth
              disabled
              name='origination_uri'
              label='Originating URL'
              sx={{
                backgroundColor: 'white',
                '& .MuiInputBase-input.Mui-disabled': {
                  WebkitTextFillColor: '#333'
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <ClickAwayListener onClickAway={handleTooltipClose}>
                      <Tooltip
                        PopperProps={{ disablePortal: true }}
                        onClose={handleTooltipClose}
                        open={open}
                        disableFocusListener
                        disableHoverListener
                        disableTouchListener
                        placement='top'
                        title='Copied'
                      >
                        <IconButton onClick={handleTooltipOpen}>
                          <CopyIcon />
                        </IconButton>
                      </Tooltip>
                    </ClickAwayListener>
                  </InputAdornment>
                )
              }}
            />
            <Typography fontSize='12px' color='text.secondary' mt={0.5}>
              Use this URI to configure your SIP endpoint.
            </Typography>
          </Grid>
        )}
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: 1 }}>
        <Button
          variant='contained'
          isPending={creatingInboundSip || updatingInboundSip}
          color='primary'
          onClick={handleSubmit(onSubmit)}
          sx={{ textTransform: 'none', borderRadius: 6, px: 3, fontWeight: 500 }}
        >
          {inboundFormData?.id && !persist ? 'Update Configuration' : 'Save Configuration'}
        </Button>
      </Box>
    </>
  );
}
